/**
 * Shared layout for public/legal/marketing pages.
 * Centralises header + footer so each leaf page is a focused content file.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import ScensobConnectLogo from '../../components/brand/ScensobConnectLogo';

const FOOTER_GROUPS = [
  {
    title: 'Company',
    links: [
      { to: '/about', label: 'About Us' },
      { to: '/careers', label: 'Careers' },
      { to: '/contact', label: 'Contact' },
      { to: '/blog', label: 'Blog' },
      { to: '/enterprise', label: 'Enterprise' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/terms', label: 'Terms of Service' },
      { to: '/hipaa', label: 'HIPAA Notice' },
      { to: '/cookies', label: 'Cookies' },
      { to: '/security', label: 'Security' },
    ],
  },
  {
    title: 'Get started',
    links: [
      { to: '/register', label: 'Create account' },
      { to: '/login', label: 'Sign in' },
    ],
  },
];

export default function PublicLayout({ title, eyebrow, children, lastUpdated }) {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col" data-testid="public-layout">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" aria-label="Header ScensobConnect home">
            <ScensobConnectLogo variant="mark" size={36} />
            <span className="font-bold text-lg">ScensobConnect</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/login" aria-label="Header Sign In" className="text-slate-600 hover:text-slate-900">Sign in</Link>
            <Link to="/register" className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium">Get started</Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-6 py-12 lg:py-16">
          {eyebrow && (
            <p className="text-xs uppercase tracking-wider text-violet-600 font-semibold mb-2" data-testid="page-eyebrow">{eyebrow}</p>
          )}
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3" data-testid="page-title">{title}</h1>
          {lastUpdated && (
            <p className="text-sm text-slate-500 mb-8">Last updated: {lastUpdated}</p>
          )}
          <article className="prose prose-slate max-w-none">
            {children}
          </article>
        </div>
      </main>

      <footer className="bg-slate-900 text-slate-300 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ScensobConnectLogo variant="mark" size={32} />
              <span className="font-bold text-white">ScensobConnect</span>
            </div>
            <p className="text-sm text-slate-400">Enterprise staffing marketplace for healthcare and group-home operators.</p>
          </div>
          {FOOTER_GROUPS.map((g) => (
            <div key={g.title}>
              <h3 className="text-white font-semibold mb-3 text-sm">{g.title}</h3>
              <ul className="space-y-2 text-sm">
                {g.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="hover:text-white transition" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-6xl mx-auto px-6 py-4 text-xs text-slate-500 flex items-center justify-between">
            <span>© {new Date().getFullYear()} ScensobConnect, Inc. All rights reserved.</span>
            <span>SOC 2 in progress · HIPAA aware</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
